import {
  emptyPaymentMethodDraft,
  paymentMethodUsesBank,
  paymentReferenceForSubmit,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import type { SalePaymentLinePayload, SalePaymentMethod } from "@/lib/sales/salesStore";

export type SalePaymentLine = PaymentMethodDraft & {
  clientId: string;
  amount: number;
  bankName?: string;
  paymentMode?: "token" | "full" | "partial" | "installment";
  notes?: string;
  paymentDate?: string;
};

export function newSalePaymentLine(amount = 0): SalePaymentLine {
  return {
    clientId: crypto.randomUUID(),
    amount,
    bankName: "",
    ...emptyPaymentMethodDraft(),
  };
}

export function salePaymentLinesTotal(lines: SalePaymentLine[]): number {
  return Math.round(lines.reduce((sum, line) => sum + (line.amount > 0 ? line.amount : 0), 0) * 100) / 100;
}

export function paymentMethodLabel(method: string | null | undefined): string {
  switch (method) {
    case "cash":
      return "Cash";
    case "online":
      return "Online";
    case "cheque":
      return "Cheque";
    case "pay_order":
      return "Pay Order";
    case "cash_deposit":
      return "Cash Deposit";
    default:
      return method?.trim() ? method : "—";
  }
}

export function salePaymentLineReference(line: SalePaymentLine): string | null {
  if (line.paymentMethod === "online" && line.transactionReferenceNo.trim()) {
    return `Txn: ${line.transactionReferenceNo.trim()}`;
  }
  if (line.paymentMethod === "pay_order" && line.payOrderNo.trim()) {
    return `PO: ${line.payOrderNo.trim()}`;
  }
  if (line.paymentMethod === "cheque" && line.chequeNo?.trim()) {
    return `Chq: ${line.chequeNo.trim()}`;
  }
  if (line.paymentMethod === "cash_deposit" && line.depositSlipNo?.trim()) {
    return `Slip: ${line.depositSlipNo.trim()}`;
  }
  return null;
}

export function validateSalePaymentLine(
  line: SalePaymentLine,
  opts: { maxAmount: number; otherLinesTotal: number },
): string | null {
  if (!Number.isFinite(line.amount) || line.amount <= 0) {
    return "Enter a valid amount greater than zero.";
  }
  const maxForLine = Math.max(0, Math.round((opts.maxAmount - opts.otherLinesTotal) * 100) / 100);
  if (line.amount > maxForLine + 0.009) {
    return `Amount cannot exceed the remaining ${maxForLine.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`;
  }
  if (!line.paymentMethod) {
    return "Select a payment method.";
  }
  if (paymentMethodUsesBank(line.paymentMethod) && !line.bankId.trim()) {
    return "Select a bank for this payment method.";
  }
  return null;
}

export function salePaymentLineToPayload(line: SalePaymentLine): SalePaymentLinePayload {
  return {
    amount: line.amount,
    payment_method: (line.paymentMethod || null) as SalePaymentMethod | null,
    bank_id: line.bankId.trim() ? Number(line.bankId) : null,
    transaction_reference_no: paymentReferenceForSubmit(line),
    pay_order_no: line.paymentMethod === "pay_order" ? line.payOrderNo.trim() || null : null,
    payment_mode: line.paymentMode || null,
    notes: line.notes?.trim() || null,
    payment_date: line.paymentDate || null,
  };
}

export function buildSalePaymentsPayload(lines: SalePaymentLine[]): SalePaymentLinePayload[] {
  return lines.filter((line) => line.amount > 0).map(salePaymentLineToPayload);
}
